import React from 'react';
import {
  IconArrowDown,
  IconPublish,
  IconTitle,
  IconHeightS,
  IconListBulleted,
} from '@bynder/icons';
import {
  Inspector,
  List,
  Thumbnail,
  Tabs,
  Divider,
  notify,
  Tooltip,
} from '@bynder/design-system';
import { Translate, useTranslate } from '@bynder/localization';
import {
  useActiveChapter,
  useActiveGuide,
  useActivePage,
} from '../../../../queries/guides';
import {
  useActiveThemeset,
  useGetThemesetColors,
} from '../../../../queries/themesets';
import { usePageMutations } from '../../../../mutations/pages';
import { PageHeaderHeights } from '../../../../types/headerHeights';
import { ImageExtensions } from '../../../../constants/widgets';
import {
  Cover,
  CVAsset,
  CVFile,
  ModalType,
  Page,
  PageHeaderOptions,
  PageStates,
} from '../../../../types';
import {
  defaultTextColor,
  defaultWhite,
} from '../../../widgets/RichText/Config';
import { useChapterMutations } from '../../../../mutations/widgets/chapters';
import {
  PublishPageSuccessNotification,
  UnpublishPageSuccessNotification,
} from '../../../../types/notifications';
import { ListArrow } from '../../Styles';
import ImageInspectorPanelSelector from '../WidgetSettings/ImageInspectorPanelSelector';
import { UploadType } from '../../../../hooks/useUploader';
import { useModalStore } from '../../../../stores/modalStore';
import { TableOfContents } from '../../../../types/tableOfContents';
import { InspectorTitleEditor } from '../InspectorTitleEditor';
import { Color } from '../../../../types/color';
import { useHomepageMutations } from '../../../../mutations/homepage';
import InspectorPanelColorPicker from '../ColorPicker';
import { useGetCurrentHomepage } from '../../../../queries/homepage';

export const PageSettings: React.FC<{ page: Page }> = ({ page }) => {
  const isPublished = page.state === PageStates.PUBLISHED;
  const isHeaderVisible = page.header?.isVisible;
  const { data: themeset } = useActiveThemeset();

  if (!themeset) return null;
  return (
    <>
      <PublishLandingPageToggle isPublished={isPublished} />
      <ShowHeaderToggle pageHeader={page.header} />
      {isHeaderVisible && (
        <>
          <Inspector.Divider />
          <TitleToggle page={page} />

          <Inspector.Divider />
          <PageHeaderHeightSelector pageHeader={page.header}>
            <PageHeaderImage pageHeader={page.header} />
            <Divider />
            <PageBackgroundColorSelector
              pageHeader={page.header}
              defaultBackgroundColor={
                themeset.pageHeader.backgroundColor ?? defaultWhite
              }
            />
          </PageHeaderHeightSelector>
        </>
      )}
      <Inspector.Divider />
      <TableOfContentsSettings tableOfContents={page.tableOfContents} />
    </>
  );
};

const ShowHeaderToggle: React.FC<{ pageHeader: PageHeaderOptions }> = ({
  pageHeader,
}) => {
  const { editPageHeaderVisibility } = usePageMutations();

  const handleShowHeaderToggle = () => {
    editPageHeaderVisibility.mutate({ isVisible: !pageHeader.isVisible });
  };

  return (
    <List.Item
      checkedVariant="switch"
      isChecked={pageHeader.isVisible}
      onClick={handleShowHeaderToggle}
      subtext="Show header in page"
      data-testid={pageHeader.isVisible ? 'headerVisible' : 'headerHidden'}
    >
      Header
    </List.Item>
  );
};

export const PublishAudiencePageToggle: React.FC<{
  isPublished: boolean;
}> = ({ isPublished }) => {
  const { publishPage, unpublishPage } = usePageMutations();
  const { data: guide } = useActiveGuide();
  const { data: currentHomepage } = useGetCurrentHomepage();
  const isActiveHomepage = currentHomepage === guide?.id;

  const handlePublishPage = () => {
    if (!isPublished) {
      publishPage.mutate();
      notify(PublishPageSuccessNotification);
    } else {
      unpublishPage.mutate();
      notify(UnpublishPageSuccessNotification);
    }
  };
  const Toggle = () => (
    <List.Item
      thumbnail={<Thumbnail shape="square" icon={<IconPublish />} />}
      checkedVariant="switch"
      isChecked={isPublished}
      isDisabled={!isActiveHomepage}
      onClick={handlePublishPage}
      data-testid={isPublished ? 'pagePublished' : 'pageUnpublished'}
    >
      <Translate id="PAGE_HEADER.PUBLISH_BUTTON_TEXT" />
    </List.Item>
  );
  if (!isActiveHomepage)
    return (
      <Tooltip
        content={
          <Translate id="HOMEPAGE_INSPECTOR.PUBLISH_AUDIENCE_DISABLED" />
        }
      >
        <Toggle />
      </Tooltip>
    );
  return <Toggle />; // Render the toggle normally if active homepage
};

export const PublishLandingPageToggle: React.FC<{
  isPublished: boolean;
}> = ({ isPublished }) => {
  const { publishPage, unpublishPage } = usePageMutations();

  const handlePublishPage = () => {
    if (!isPublished) {
      publishPage.mutate();
      notify(PublishPageSuccessNotification);
    } else {
      unpublishPage.mutate();
      notify(UnpublishPageSuccessNotification);
    }
  };

  return (
    <List.Item
      thumbnail={<Thumbnail shape="square" icon={<IconPublish />} />}
      checkedVariant="switch"
      isChecked={isPublished}
      onClick={handlePublishPage}
      data-testid={isPublished ? 'pagePublished' : 'pageUnpublished'}
    >
      <Translate id="PAGE_HEADER.PUBLISH_BUTTON_TEXT" />
    </List.Item>
  );
};

export const SetHomepageToggle: React.FC = () => {
  const { data: guide } = useActiveGuide();
  const openModal = useModalStore.use.openModal();
  const closeModal = useModalStore.use.closeModal();
  const { setHomepage, unsetHomepage } = useHomepageMutations();
  const { data: currentHomepage } = useGetCurrentHomepage();
  const isActiveHomePage = currentHomepage === guide?.id;

  const setHomepageSubmit = () => {
    setHomepage.mutate({ guideId: guide!.id, guideVersion: guide!.version });
    closeModal();
  };

  const unsetHomepageSubmit = () => {
    unsetHomepage.mutate({ guideId: guide!.id, guideVersion: guide!.version });
    closeModal();
  };

  const handleSetHomepage = () => {
    if (isActiveHomePage) {
      openModal({
        modalType: ModalType.UNSET_HOMEPAGE_MODAL,
        modalOnSubmit: () => unsetHomepageSubmit(),
      });
    } else {
      openModal({
        modalType: ModalType.HOMEPAGE_MODAL,
        modalOnSubmit: () => setHomepageSubmit(),
      });
    }
  };

  return (
    <List.Item
      thumbnail={<Thumbnail shape="square" icon={<IconPublish />} />}
      checkedVariant="switch"
      isChecked={isActiveHomePage}
      onClick={handleSetHomepage}
    >
      <Translate id="PAGE_HEADER.PUBLISH_BUTTON_TEXT" />
    </List.Item>
  );
};

const TitleToggle: React.FC<{ page: Page }> = ({ page }) => {
  const { editPageTitleVisibility, editChapterTitleVisibility } =
    usePageMutations();
  const { translate } = useTranslate();
  const chapter = useActiveChapter();
  const { editPageTitle } = usePageMutations();
  const { renameChapter } = useChapterMutations();
  const [isExpanded, setIsExpanded] = React.useState(true);

  if (!chapter) return null;

  const handleEditPageTitle = (newTitle: string) => {
    editPageTitle.mutate({ newTitle, pageId: page.id });
  };

  const handleEditChapterTitle = (newTitle: string) => {
    renameChapter.mutate({
      chapterId: chapter?.id,
      newChapterTitle: newTitle,
    });
  };

  return (
    <>
      <List.Item
        thumbnail={<Thumbnail shape="square" icon={<IconTitle />} />}
        rightElements={
          <ListArrow aria-label="Toggle" rotate={isExpanded ? 180 : 0}>
            <IconArrowDown />
          </ListArrow>
        }
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Translate id="PAGE_INSPECTOR.TITLE_SECTION" />
      </List.Item>
      {isExpanded && (
        <>
          <List.Item
            checkedVariant="switch"
            data-testid={
              page.header?.isSubtitleVisible
                ? 'chapterTitleVisible'
                : 'chapterTitleHidden'
            }
            isChecked={page.header?.isSubtitleVisible}
            onClick={() =>
              editChapterTitleVisibility.mutate({
                isVisible: !page.header?.isSubtitleVisible,
              })
            }
          >
            <Translate id="PAGE_INSPECTOR.CHAPTER_TITLE" />
          </List.Item>
          <InspectorTitleEditor
            testId="chapterTitle"
            onEdit={handleEditChapterTitle}
            propTitle={chapter.title}
            subText="Title"
          />

          <Divider />
          <List.Item
            checkedVariant="switch"
            data-testid={
              page?.header?.isTitleVisible
                ? 'pageTitleVisible'
                : 'pageTitleHidden'
            }
            isChecked={page?.header?.isTitleVisible}
            onClick={() =>
              editPageTitleVisibility.mutate({
                isVisible: !page?.header?.isTitleVisible,
              })
            }
          >
            <Translate id="PAGE_INSPECTOR.PAGE_TITLE" />
          </List.Item>
          <InspectorTitleEditor
            testId="pageTitle"
            onEdit={handleEditPageTitle}
            propTitle={page.title}
            subText={translate('GUIDE_INSPECTOR_PANEL.GUIDE_TITLE')}
          />
          <List.Divider />
          <PageHeaderTextColorPicker
            pageHeaderColorId={page.header.textColorId}
          />
        </>
      )}
    </>
  );
};

const PageHeaderHeightSelector: React.FC<{
  children: React.ReactNode;
  pageHeader: PageHeaderOptions;
}> = ({ children, pageHeader }) => {
  const { translate } = useTranslate();
  const { editPageHeaderHeight } = usePageMutations();
  const [isExpanded, setIsExpanded] = React.useState(true);

  const pageHeight = pageHeader.height;

  const handlePageHeaderHeight = (
    selectedPageHeaderHeight: PageHeaderHeights,
  ) => {
    editPageHeaderHeight.mutate({ selectedPageHeaderHeight });
  };

  return (
    <>
      <List.Item
        thumbnail={<Thumbnail shape="square" icon={<IconHeightS />} />}
        rightElements={
          <ListArrow aria-label="Toggle" rotate={isExpanded ? 180 : 0}>
            <IconArrowDown />
          </ListArrow>
        }
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Translate id="PAGE_INSPECTOR.HEADER_TYPE_TITLE" />
      </List.Item>
      {isExpanded && (
        <>
          <List.Group title={translate('PAGE_INSPECTOR.HEADER_HEIGHT_TITLE')}>
            <Tabs variant="clean">
              <Tabs.Tab
                label="S"
                data-testid="pageHeaderHeightSmall"
                onClick={() =>
                  handlePageHeaderHeight(PageHeaderHeights.DEFAULT)
                }
                isActive={pageHeight === PageHeaderHeights.DEFAULT}
              />
              <Tabs.Tab
                label="M"
                data-testid="pageHeaderHeightMedium"
                onClick={() => handlePageHeaderHeight(PageHeaderHeights.MEDIUM)}
                isActive={pageHeight === PageHeaderHeights.MEDIUM}
              />
              <Tabs.Tab
                label="L"
                data-testid="pageHeaderHeightLarge"
                onClick={() => handlePageHeaderHeight(PageHeaderHeights.LARGE)}
                isActive={pageHeight === PageHeaderHeights.LARGE}
              />
            </Tabs>
          </List.Group>
          {children}
        </>
      )}
    </>
  );
};

const PageHeaderImage: React.FC<{ pageHeader: PageHeaderOptions }> = ({
  pageHeader,
}) => {
  const { translate } = useTranslate();
  const { placeAssetInPageHeader, uploadAssetInPageHeader } =
    usePageMutations();
  const pageCover = pageHeader?.asset?.imageUrl;

  const handleChangePageCover = (selectedMedia: CVAsset[], file: CVFile) => {
    if (selectedMedia.length) {
      const { databaseId, name, url, extensions, derivatives } =
        selectedMedia[0];
      let fileUrl;

      if (!(extensions[0] in ImageExtensions)) {
        fileUrl = derivatives.webImage;
      } else {
        fileUrl = file.selectedFile.url;
      }

      placeAssetInPageHeader.mutate({
        cover: {
          assetId: databaseId,
          assetName: name,
          detailUrl: url,
          imageUrl: fileUrl,
        },
      });
    }
  };

  const clearPageCover = () => {
    placeAssetInPageHeader.mutate({
      cover: {
        assetId: '',
        assetName: '',
        detailUrl: '',
        imageUrl: '',
      },
    });
  };

  const setPageCover = (imageUrl: string) => {
    const asset = pageHeader.asset as Cover;
    const { assetId = null, assetName = null, detailUrl = null } = asset;

    placeAssetInPageHeader.mutate({
      cover: {
        assetId,
        assetName,
        detailUrl,
        imageUrl,
      },
    });
  };

  const getPageHeaderImageSubtext = () => {
    if (pageHeader.height === PageHeaderHeights.LARGE) {
      return translate('PAGE_INSPECTOR.HEADER_SUBTEXT_L');
    } else if (pageHeader.height === PageHeaderHeights.MEDIUM) {
      return translate('PAGE_INSPECTOR.HEADER_SUBTEXT_M');
    }

    return translate('PAGE_INSPECTOR.HEADER_SUBTEXT_S');
  };

  const handleUploadCompleted = (mediaId: string) => {
    uploadAssetInPageHeader.mutate({ mediaId });
  };

  return (
    <>
      <Divider />
      <List.Item subtext={getPageHeaderImageSubtext()}>
        <Translate id="PAGE_INSPECTOR.HEADER_IMAGE_TITLE" />
      </List.Item>
      <ImageInspectorPanelSelector
        uploadType={UploadType.PageBackground}
        handleUploadCompleted={handleUploadCompleted}
        handleAssetSelect={handleChangePageCover}
        selectedAssetId={pageHeader.asset?.assetId}
        handleOnClearImage={clearPageCover}
        handleOnSetImage={setPageCover}
        imageUrl={pageCover ? pageCover : null}
        assetName={pageHeader.asset?.assetName}
      />
    </>
  );
};

const PageBackgroundColorSelector: React.FC<{
  pageHeader: PageHeaderOptions;
  defaultBackgroundColor: Color;
}> = ({ pageHeader, defaultBackgroundColor }) => {
  const customColors = useGetThemesetColors();
  const { editPageHeaderBackgroundColor } = usePageMutations();

  const activeHeaderColor = customColors.find(
    color => color.id === pageHeader.backgroundColor,
  );

  const handleEditBackgroundColor = (backgroundColorId: string | null) => {
    editPageHeaderBackgroundColor.mutate({ backgroundColorId });
  };

  return (
    <>
      <List.Item>
        <Translate id="PAGE_INSPECTOR.HEADER_BACKGROUND_TITLE" />
      </List.Item>

      <InspectorPanelColorPicker
        testId="pageHeaderBackgroundColor"
        defaultColorHex={defaultBackgroundColor.hexCode}
        activeColor={activeHeaderColor}
        handleClick={handleEditBackgroundColor}
        handleClear={() => {
          handleEditBackgroundColor(null);
        }}
        label={activeHeaderColor?.label ?? 'Default theme'}
        colors={customColors}
      />
    </>
  );
};

const TableOfContentsSettings: React.FC<{
  tableOfContents: TableOfContents;
}> = ({ tableOfContents }) => {
  const { data: page } = useActivePage();
  const [isExpanded, setIsExpanded] = React.useState(true);

  const { editTableOfContentsVisibility } = usePageMutations();

  const toggleGuideVisibility = () => {
    editTableOfContentsVisibility.mutate({
      isVisibleInGuide: !tableOfContents.isVisibleInGuide,
      isVisibleInPage: tableOfContents.isVisibleInPage,
    });
  };

  const togglePageVisibility = () => {
    editTableOfContentsVisibility.mutate({
      isVisibleInGuide: tableOfContents.isVisibleInGuide,
      isVisibleInPage: !tableOfContents.isVisibleInPage,
    });
  };

  return (
    <>
      <List.Item
        thumbnail={<Thumbnail shape="square" icon={<IconListBulleted />} />}
        rightElements={
          <ListArrow aria-label="Toggle" rotate={isExpanded ? 180 : 0}>
            <IconArrowDown />
          </ListArrow>
        }
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Translate id="PAGE_INSPECTOR.TOC_TITLE" />
      </List.Item>
      {isExpanded && (
        <>
          <List.Item
            data-testid={
              page?.tableOfContents?.isVisibleInGuide
                ? 'tocVisible'
                : 'tocHidden'
            }
            checkedVariant="switch"
            subtext={<Translate id="PAGE_INSPECTOR.TOC_SIDEBAR_DESCRIPTION" />}
            isChecked={page?.tableOfContents?.isVisibleInGuide}
            onClick={toggleGuideVisibility}
          >
            <Translate id="PAGE_INSPECTOR.TOC_TITLE" />
          </List.Item>

          <List.Item
            data-testid={
              page?.tableOfContents?.isVisibleInPage
                ? 'pageTocVisible'
                : 'pageTocHidden'
            }
            checkedVariant="switch"
            subtext={<Translate id="PAGE_INSPECTOR.TOC_PAGE_DESCRIPTION" />}
            isChecked={page?.tableOfContents?.isVisibleInPage}
            onClick={togglePageVisibility}
          >
            <Translate id="PAGE_INSPECTOR.TOC_PAGE_TITLE" />
          </List.Item>
        </>
      )}
    </>
  );
};

const PageHeaderTextColorPicker: React.FC<{
  pageHeaderColorId: string | null;
}> = ({ pageHeaderColorId }) => {
  const customColors = useGetThemesetColors();
  const { data: themeset } = useActiveThemeset();
  const activeTextColor = customColors.find(
    color => color.id === pageHeaderColorId,
  );
  const { editPageHeaderTextColor } = usePageMutations();
  const onEditTextColor = (textColorId: string | null) => {
    editPageHeaderTextColor.mutate({ textColorId });
  };

  const getDefaultPageHeaderColor = () => {
    if (themeset) {
      const fallbackColor = defaultTextColor;
      const typographyH2Color = themeset?.typographies[1]?.fontColor;
      const themesetTextColor = themeset?.pageHeader.textColor;

      if (themesetTextColor) {
        return themesetTextColor;
      } else if (typographyH2Color) {
        return typographyH2Color;
      } else {
        return fallbackColor;
      }
    }
    return defaultTextColor;
  };
  return (
    <>
      <List.Item>
        <Translate id="PAGE_INSPECTOR.HEADER_TEXT_COLOR_TITLE" />
        <InspectorPanelColorPicker
          testId="pageHeaderTextColor"
          defaultColorHex={getDefaultPageHeaderColor().hexCode}
          activeColor={activeTextColor}
          handleClick={onEditTextColor}
          handleClear={() => {
            onEditTextColor(null);
          }}
          label={activeTextColor ? activeTextColor.label : 'Default theme'}
          colors={customColors}
        />
      </List.Item>
    </>
  );
};
